/**
 * Consent Verification — main component.
 *
 * Shows what data a partner is requesting access to.
 * User can allow or decline consent.
 *
 * States:
 * - initializing: Waiting for IDME_INIT postMessage from parent
 * - ready: Consent UI shown, waiting for user action
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getAllowedDomains } from "../../shared/utils/apiConfig";
import { LanguageSelector } from "../faceVerification/LanguageSelector";
import { logger } from "../../shared/utils/logger";
import styles from "./ConsentVerification.module.scss";
import { useConsumer } from "../ConsumerProvider";
import { BRAND_HEADER_ID } from "../brandLogo/BrandLayout";

export type ConsentVerificationState = "initializing" | "ready";

interface ConsentAttribute {
  handle: string;
  description: string;
}

interface InitPayload {
  partnerName: string;
  partnerLogoUrl?: string;
  attributes?: ConsentAttribute[];
}

function normalizeLabel(label: string, isFirst: boolean): string {
  let s = label.toLowerCase().replace("social security", "Social Security");
  if (isFirst) s = s.charAt(0).toUpperCase() + s.slice(1);
  return s;
}

function buildScopeSummary(attrs: ConsentAttribute[], andMore: string): string {
  const MAX_SHOWN = 3;
  const shown = attrs.slice(0, MAX_SHOWN).map((a, i) => normalizeLabel(a.description, i === 0));
  const hasMore = attrs.length > MAX_SHOWN;
  return hasMore ? `${shown.join(", ")}, ${andMore}.` : `${shown.join(", ")}.`;
}

const PersonIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20 21C20 19.6044 20 18.9067 19.8278 18.3389C19.44 17.0605 18.4395 16.06 17.1611 15.6722C16.5933 15.5 15.8956 15.5 14.5 15.5H9.5C8.10444 15.5 7.40665 15.5 6.83886 15.6722C5.56045 16.06 4.55996 17.0605 4.17224 18.3389C4 18.9067 4 19.6044 4 21M16.5 7.5C16.5 9.98528 14.4853 12 12 12C9.51472 12 7.5 9.98528 7.5 7.5C7.5 5.01472 9.51472 3 12 3C14.4853 3 16.5 5.01472 16.5 7.5Z"
      stroke="#004EEB"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function ConsentVerification() {
  const { t } = useTranslation();
  const { fragnoLogoEnabled } = useConsumer();
  const [state, setState] = useState<ConsentVerificationState>("initializing");
  const [initData, setInitData] = useState<InitPayload | null>(null);
  const [expanded, setExpanded] = useState(false);
  const initDataRef = useRef<InitPayload | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  // Resolved once we have a validated sender: first from document.referrer (mount),
  // then locked to the verified IDME_INIT sender origin. Never falls back to "*" for
  // messages that carry sensitive payload (VERIFICATION_COMPLETE / VERIFICATION_ERROR).
  const parentOriginRef = useRef<string | null>(null);


  // Best-effort parent origin from document.referrer on mount.
  // This is only a heuristic — document.referrer can be empty (privacy settings,
  // same-page nav, certain redirects). The definitive origin is set when a validated
  // IDME_INIT arrives (see handler below).
  useEffect(() => {
    if (!document.referrer) return;
    try {
      const origin = new URL(document.referrer).origin;
      const hostname = new URL(document.referrer).hostname;
      const isAllowed = getAllowedDomains().some(
        (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
      );
      if (isAllowed) {
        parentOriginRef.current = origin;
        logger.log("[ConsentVerification] Parent origin set from referrer:", origin);
      } else {
        logger.warn("[ConsentVerification] Referrer not in allowlist:", origin);
      }
    } catch {
      logger.warn("[ConsentVerification] Could not parse document.referrer:", document.referrer);
    }
  }, []);

  useEffect(() => {
    initDataRef.current = initData;
  }, [initData]);

  const handleAllow = useCallback(() => {
    const data = initDataRef.current;
    const targetOrigin = parentOriginRef.current;
    if (!data || !targetOrigin) return;
    logger.log("[ConsentVerification] User allowed consent for:", data.partnerName);
    window.parent.postMessage(
      {
        source: "idme-verification",
        type: "VERIFICATION_COMPLETE",
        payload: { allowed: true, partnerName: data.partnerName },
      },
      targetOrigin
    );
  }, []);

  const handleDecline = useCallback(() => {
    const data = initDataRef.current;
    const targetOrigin = parentOriginRef.current;
    if (!data || !targetOrigin) return;
    logger.log("[ConsentVerification] User declined consent for:", data.partnerName);
    window.parent.postMessage(
      {
        source: "idme-verification",
        type: "VERIFICATION_ERROR",
        payload: { errorCode: "user_declined", message: "User declined consent" },
      },
      targetOrigin
    );
  }, []);

  // Send IDME_READY, then listen for IDME_INIT.
  // IDME_READY carries no sensitive payload so "*" is acceptable here — we do not
  // yet know the parent's exact origin and the message contains no user data.
  useEffect(() => {
    window.parent.postMessage(
      { source: "idme-verification", type: "IDME_READY" },
      "*"
    );

    const handler = (event: MessageEvent) => {
      if (!event.data || event.data.type !== "IDME_INIT") return;
      try {
        const senderHostname = new URL(event.origin).hostname;
        const allowed = getAllowedDomains().some(
          (d) => senderHostname === d || senderHostname.endsWith(`.${d}`)
        );
        if (!allowed) {
          logger.warn("[ConsentVerification] IDME_INIT rejected from origin:", event.origin);
          return;
        }
        // Lock parentOrigin to this validated sender. All subsequent sensitive
        // postMessages (VERIFICATION_COMPLETE, VERIFICATION_ERROR) will use this
        // exact origin, eliminating the "*" targetOrigin exposure.
        parentOriginRef.current = event.origin;
        logger.log("[ConsentVerification] Parent origin locked from IDME_INIT:", event.origin);
      } catch {
        logger.warn("[ConsentVerification] Could not parse IDME_INIT origin:", event.origin);
        return;
      }
      const payload = event.data.payload as InitPayload;
      logger.log("[ConsentVerification] Received init:", payload.partnerName);
      setInitData(payload);
      initDataRef.current = payload;
      setState("ready");
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  useEffect(() => {
    if (state === "ready") {
      // With the Fragno logo on, the header lives in BrandLayout (VEX-7983).
      (headerRef.current ?? document.getElementById(BRAND_HEADER_ID))?.focus();
    }
  }, [state]);

  const attributes = initData?.attributes ?? [];
  const partnerName = initData?.partnerName ?? "";

  return (
    <div className={styles.pageContainer}>
      <div className={styles.container}>
        <div className={styles.contentContainer}>

          {/* IDMe Logo */}
          {/* VEX-7983: with the Fragno logo on, BrandLayout renders the shared header instead */}
          {!fragnoLogoEnabled && (
            <header ref={headerRef} tabIndex={-1} className={styles.logoContainer}>
              <div className={styles.logo}>
                {initData?.partnerLogoUrl ? (
                  <img src={initData.partnerLogoUrl} alt={partnerName} />
                ) : (
                  <img src="/assets/svg/idme-logo.svg" alt="IDMe" />
                )}
              </div>
            </header>
          )}

          {/* Card */}
          <main className={styles.card}>

            {state === "initializing" && (
              <div className={styles.spinner} />
            )}

            {state === "ready" && (
              <>
                <h1 id="consent-title" className={styles.title}>
                  {t("consent.title", { partnerName })}
                </h1>

                <p className={styles.description}>
                  {t("consent.description", { partnerName })}
                </p>

                {/* Attributes card */}
                {attributes.length > 0 && (
                  <div className={styles.scopeCard}>
                    <div className={styles.scopeRow}>
                      <div className={styles.scopeIconWrap} aria-hidden="true">
                        <PersonIcon />
                      </div>
                      <div className={styles.scopeTextWrap}>
                        <p className={styles.scopeTitle}>
                          {t("consent.defaultScopes.legalProfile.title")}
                        </p>
                        {!expanded && (
                          <p className={styles.scopeSubtitle}>
                            {buildScopeSummary(attributes, t("consent.toggle.andMore"))}
                          </p>
                        )}
                        {expanded && (
                          <ul className={styles.scopeItems}>
                            {attributes.map((attr) => (
                              <li key={attr.handle} className={styles.scopeItem}>
                                {attr.description}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                    {expanded && (
                      <p className={styles.walletNote}>
                        {t("consent.walletNote")}
                      </p>
                    )}
                    <button
                      className={styles.toggleLink}
                      onClick={() => setExpanded(!expanded)}
                      type="button"
                      aria-expanded={expanded}
                    >
                      {expanded ? t("consent.toggle.viewLess") : t("consent.toggle.viewDetails")}
                    </button>
                  </div>
                )}

                {/* CTA buttons */}
                <button className={styles.allowButton} onClick={handleAllow} type="button" aria-labelledby={partnerName ? "consent-title" : undefined}>
                  {t("consent.allowButton")}
                </button>
                <button className={styles.declineButton} onClick={handleDecline} type="button" aria-label={partnerName ? t("consent.declineButtonAriaLabel", { partnerName }) : undefined}>
                  {t("consent.declineButton")}
                </button>
              </>
            )}
          </main>

          {/* Footer */}
          <footer className={styles.footer}>
            <LanguageSelector />
            <div className={styles.footerLinks}>
              <a href="https://www.id.me/about" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>{t("consent.footer.whatIsIdme")}</a>
              <span aria-hidden="true" className={styles.footerDivider}>|</span>
              <a href="https://www.id.me/terms" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>{t("consent.footer.termsOfService")}</a>
              <span aria-hidden="true" className={styles.footerDivider}>|</span>
              <a href="https://www.id.me/privacy" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>{t("consent.footer.privacyPolicy")}</a>
            </div>
          </footer>

        </div>
      </div>
    </div>
  );
}
